// Styled in global.css. An attribute rather than classes, because React keeps attributes it
// doesn't render when it re-renders an element with new classes.
const HIGHLIGHT_ATTRIBUTE = 'data-settings-highlight';
const HIGHLIGHT_MS = 3000;
// Long enough for a settings page to load the data its cards wait for.
const WAIT_FOR_ANCHOR_MS = 10_000;
const USER_INPUT_EVENTS = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;

/** Sent to a revealed element so a closed disclosure can open. */
export const SETTINGS_REVEAL_EVENT = 'settings-reveal';

let cancelPreviousReveal = () => {};

function scrollToElement(element: HTMLElement) {
  // Not smooth: that scroll never runs in a tab opened in the background. A card taller than half
  // the screen lines up with the top so its title stays in view.
  element.scrollIntoView({
    block: element.offsetHeight > window.innerHeight / 2 ? 'start' : 'center',
  });
}

/**
 * Scrolls the settings element with this id into view and highlights it briefly. A link can open
 * a page that renders the element later, so this waits for it. Cards that finish loading above it
 * push it down, so it stays in view while highlighted, until the user scrolls or clicks. A later
 * call cancels this one.
 */
export function revealSettingsAnchor(anchor: string) {
  cancelPreviousReveal();
  let element: HTMLElement | null = null;
  let highlightTimer: ReturnType<typeof setTimeout> | undefined;
  let giveUpTimer = setTimeout(stopFollowing, WAIT_FOR_ANCHOR_MS);
  const observer = new MutationObserver(update);

  // React can swap the element for another with the same id, as when a placeholder card gives way
  // to the real one, so this looks it up again on every change.
  function update() {
    const found = document.getElementById(anchor);
    // The page a link leaves stays mounted but hidden, and may hold the same element.
    const visible = found && found.checkVisibility?.() !== false ? found : null;
    if (visible !== element) {
      element?.removeAttribute(HIGHLIGHT_ATTRIBUTE);
      element = visible;
      clearTimeout(giveUpTimer);
      clearTimeout(highlightTimer);
      if (element) {
        element.setAttribute(HIGHLIGHT_ATTRIBUTE, '');
        element.dispatchEvent(new Event(SETTINGS_REVEAL_EVENT));
        highlightTimer = setTimeout(cancel, HIGHLIGHT_MS);
      } else {
        giveUpTimer = setTimeout(stopFollowing, WAIT_FOR_ANCHOR_MS);
      }
    }
    if (element) {
      scrollToElement(element);
    }
  }

  function stopFollowing() {
    observer.disconnect();
    for (const type of USER_INPUT_EVENTS) {
      window.removeEventListener(type, stopFollowing, true);
    }
  }

  function cancel() {
    stopFollowing();
    clearTimeout(giveUpTimer);
    clearTimeout(highlightTimer);
    element?.removeAttribute(HIGHLIGHT_ATTRIBUTE);
  }

  cancelPreviousReveal = cancel;
  for (const type of USER_INPUT_EVENTS) {
    window.addEventListener(type, stopFollowing, { capture: true, passive: true });
  }
  // A hidden page that shows again changes only its style. The highlight attribute stays
  // unwatched, or setting it would call this again without end.
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style'],
  });
  update();
}
